import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import socketService from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { Badge, Button, Input, showToast } from '../components/UI';

export default function ListingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    api.getListing(id)
      .then(setListing)
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));

    // Real-time updates
    socketService.connect();
    socketService.joinListing(id);
    socketService.on('bidUpdate', (data) => {
      if (data.listingId === id) {
        setListing(prev => {
          if (!prev) return prev;
          const updates = {
            ...prev,
            currentHighestBid: data.currentHighestBid,
            totalOffers: data.totalOffers,
          };
          if (data.auctionEndsAt) {
            updates.auctionEndsAt = data.auctionEndsAt;
          }
          return updates;
        });
        if (data.extended) {
          // Could show a toast: "Auction extended by 2 minutes!"
        }
      }
    });
    socketService.on('auctionEnded', (data) => {
      if (data.listingId === id) {
        setListing(prev => prev ? { ...prev, status: data.status === 'awarded' ? 'awarded' : 'ended' } : prev);
      }
    });

    return () => {
      socketService.leaveListing(id);
      socketService.removeAllListeners();
    };
  }, [id, navigate]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>;
  if (!listing) return null;

  const endsAt = new Date(listing.auctionEndsAt);
  const msLeft = endsAt - new Date();
  const isActive = listing.status === 'active' && msLeft > 0;
  const minBid = listing.currentHighestBid > 0 ? Number(listing.currentHighestBid) + 1 : Number(listing.startingPrice);
  const canBid = user?.role === 'buyer' && isActive && user.verificationStatus === 'verified';

  const handleBid = async () => {
    if (!user) { navigate('/login'); return; }
    const amount = Number(bidAmount);

    // Client-side check (backend enforces too)
    const currentHigh = Number(listing.currentHighestBid);
    if (currentHigh > 0 && amount <= currentHigh) {
      showToast(`You can not make lower offer than current high offer. Current highest offer is $${currentHigh.toLocaleString()}.`, 'error');
      return;
    }

    setSubmitting(true);
    try {
      await api.submitOffer(id, amount);
      showToast(`Offer of $${amount.toLocaleString()} submitted!`);
      setBidAmount('');
      const updated = await api.getListing(id);
      setListing(updated);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const imageFiles = listing?.files?.filter(f => f.fileType === 'image') || [];
  const videoFiles = listing?.files?.filter(f => f.fileType === 'video') || [];
  const allMedia = [...imageFiles, ...videoFiles];
  const activeFile = allMedia[activeFileIndex];

  return (
    <div style={{ padding: 32, maxWidth: 960, margin: '0 auto' }}>
      <button onClick={() => navigate('/')} style={{
        background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
        fontFamily: 'var(--sans)', fontSize: 14, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6,
      }}>← Back to Marketplace</button>

      {/* Lightbox */}
      {lightboxOpen && activeFile && (
        <div onClick={() => setLightboxOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
          backdropFilter: 'blur(8px)',
        }}>
          <button onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }} style={{
            position: 'absolute', top: 24, right: 24, background: 'none', border: 'none',
            color: 'white', fontSize: 28, cursor: 'pointer', zIndex: 1001,
          }}>✕</button>

          {activeFileIndex > 0 && (
            <button onClick={(e) => { e.stopPropagation(); setActiveFileIndex(i => i - 1); }} style={{
              position: 'absolute', left: 24, background: 'rgba(255,255,255,0.1)', border: 'none',
              color: 'white', fontSize: 32, cursor: 'pointer', padding: '16px 20px', borderRadius: 12,
            }}>‹</button>
          )}

          {activeFileIndex < allMedia.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); setActiveFileIndex(i => i + 1); }} style={{
              position: 'absolute', right: 24, background: 'rgba(255,255,255,0.1)', border: 'none',
              color: 'white', fontSize: 32, cursor: 'pointer', padding: '16px 20px', borderRadius: 12,
            }}>›</button>
          )}

          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh' }}>
            {activeFile.fileType === 'video' ? (
              <video src={activeFile.publicUrl} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }} />
            ) : (
              <img src={activeFile.publicUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, objectFit: 'contain' }} />
            )}
          </div>

          <div style={{ position: 'absolute', bottom: 24, color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)' }}>
            {activeFileIndex + 1} / {allMedia.length}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 32 }}>
        {/* Left — Details */}
        <div>
          {/* Main image/video */}
          <div
            onClick={() => { if (allMedia.length > 0) setLightboxOpen(true); }}
            style={{
              height: 320, background: 'var(--bg2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, marginBottom: 12,
              overflow: 'hidden', cursor: allMedia.length > 0 ? 'zoom-in' : 'default', position: 'relative',
            }}
          >
            {activeFile?.fileType === 'video' ? (
              <video src={activeFile.publicUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : activeFile?.publicUrl ? (
              <img src={activeFile.publicUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              '📦'
            )}
            {activeFile?.fileType === 'video' && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.3)', fontSize: 48,
              }}>▶</div>
            )}
            {allMedia.length > 0 && (
              <div style={{
                position: 'absolute', bottom: 12, right: 12, padding: '4px 12px', borderRadius: 100,
                background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 11, fontFamily: 'var(--mono)',
              }}>Click to enlarge</div>
            )}
          </div>

          {/* Thumbnails */}
          {allMedia.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
              {allMedia.map((f, i) => (
                <div
                  key={f.id}
                  onClick={() => setActiveFileIndex(i)}
                  style={{
                    width: 72, height: 72, borderRadius: 8, cursor: 'pointer',
                    border: i === activeFileIndex ? '2px solid var(--accent)' : '1px solid var(--border)',
                    overflow: 'hidden', position: 'relative', flexShrink: 0,
                  }}
                >
                  {f.fileType === 'video' ? (
                    <>
                      <video src={f.publicUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.4)', color: 'white', fontSize: 20,
                      }}>▶</div>
                    </>
                  ) : (
                    <img src={f.publicUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </div>
              ))}
            </div>
          )}

          <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', fontFamily: 'var(--sans)', letterSpacing: '-0.02em', marginBottom: 12 }}>
            {listing.title}
          </h1>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <Badge color={isActive ? 'green' : 'neutral'}>{isActive ? 'LIVE AUCTION' : listing.status?.toUpperCase()}</Badge>
            <Badge color="blue">{listing.condition}</Badge>
            {listing.grade && <Badge color="neutral">Grade {listing.grade}</Badge>}
            {listing.quantity && <Badge color="neutral">{listing.quantity} {listing.quantityUnit === 'lbs' ? 'lbs' : 'pcs'}</Badge>}
            <Badge color="neutral">{listing.category?.replace('_', ' ')}</Badge>
          </div>

          {listing.modelNumber && (
            <div style={{ fontSize: 13, color: 'var(--text2)', fontFamily: 'var(--mono)', marginBottom: 12 }}>
              Model: {listing.modelNumber}
            </div>
          )}

          <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 24 }}>{listing.description}</div>

          {listing.specifications && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 8 }}>Specifications</div>
              <div style={{ fontSize: 14, color: 'var(--text)', fontFamily: 'var(--mono)', lineHeight: 1.8 }}>{listing.specifications}</div>
            </div>
          )}

          <div style={{ fontSize: 13, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            Listed by <span style={{ color: 'var(--text2)' }}>Seller</span>
          </div>
        </div>

        {/* Right — Bid Panel */}
        <div>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
            padding: 28, position: 'sticky', top: 100,
          }}>
            <div style={{
              background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius)',
              padding: 14, marginBottom: 20, fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', lineHeight: 1.7,
            }}>
              <div style={{ fontWeight: 500, color: 'var(--amber)', marginBottom: 4 }}>⚠ OFFER RULES</div>
              Once submitted, offers cannot be changed or cancelled. The highest bidder when auction closes will be required to submit payment within 3 business days.
            </div>

            {isActive && (
              <>
                <Input
                  label="Your Offer (USD)" placeholder={`Min $${minBid.toLocaleString()}`}
                  type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)}
                  prefix="$" mono
                />
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4, marginBottom: 16 }}>
                  Minimum bid: ${minBid.toLocaleString()}
                </div>
                {canBid ? (
                  <Button
                    onClick={handleBid} disabled={submitting || Number(bidAmount) < minBid}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Offer →'}
                  </Button>
                ) : !user ? (
                  <Button onClick={() => navigate('/login')} style={{ width: '100%', justifyContent: 'center' }}>
                    Login to Bid →
                  </Button>
                ) : user.role !== 'buyer' ? (
                  <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: 12 }}>
                    Only verified buyers can place offers.
                  </div>
                ) : user.verificationStatus !== 'verified' ? (
                  <div style={{ fontSize: 13, color: 'var(--amber)', textAlign: 'center', padding: 12 }}>
                    Your R2 certification is pending verification.
                  </div>
                ) : null}
              </>
            )}

            {!isActive && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)', fontFamily: 'var(--sans)' }}>
                This auction has ended.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
